import React, { useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFilterDateInputClasses,
  adminFilterDropdownTriggerClass,
  adminFormTextareaClasses,
  getAdminNoteAddedByDisplay,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format datetime for Admin Notes display: "14 Jan 2026 · 10:42 AM"
const formatNoteDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} · ${timePart}`;
};

const formatDateOnly = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Helper function to generate workflow detail data
const generateWorkflowDetail = (workflowId, workflowFromState) => {
  const contentType = workflowFromState?.contentType || 'Posts';
  const status = workflowFromState?.status || 'Active';
  const version = 'v1.0';
  const lastUpdated = workflowFromState?.lastUpdated || new Date().toISOString();
  const updatedBy = workflowFromState?.updatedBy || 'Admin#12 — "Sarah K."';

  return {
    workflowId: workflowId || 'WF#0001',
    workflowName: workflowFromState?.workflowName || `${contentType} — Standard Moderation Workflow`,
    contentType,
    status,
    version,
    lastUpdated,
    updatedBy,
    defaultEntryStage: 'Received',
    defaultExitStage: 'Closed',
    description: `Standard flow for all ${contentType.toLowerCase()} moderation including reviews and enforcement actions.`,
  };
};

// Helper function to generate stages
const generateStages = () => {
  return [
    { name: 'Received', order: 1, required: true, slaTarget: '0 hours', visibleTo: 'Moderators, Super Admin' },
    { name: 'Assigned', order: 2, required: false, slaTarget: '2 hours', visibleTo: 'Moderators, Super Admin' },
    { name: 'In Review', order: 3, required: true, slaTarget: '24 hours', visibleTo: 'Moderators, Super Admin' },
    { name: 'Awaiting Evidence', order: 4, required: false, slaTarget: '48 hours', visibleTo: 'Moderators, Super Admin' },
    { name: 'Decision Required', order: 5, required: true, slaTarget: '12 hours', visibleTo: 'Moderators, Super Admin' },
    { name: 'Escalated', order: 6, required: false, slaTarget: '72 hours', visibleTo: 'Super Admin' },
    { name: 'Action Applied', order: 7, required: true, slaTarget: '4 hours', visibleTo: 'Moderators, Super Admin' },
    { name: 'Closed', order: 8, required: true, slaTarget: '0 hours', visibleTo: 'Moderators, Super Admin' },
  ];
};

// Helper function to generate transitions
const generateTransitions = () => {
  return [
    { from: 'Received', to: 'Assigned', allowedRoles: 'Moderator, Super Admin', autoManual: 'Manual', conditions: '—' },
    { from: 'Assigned', to: 'In Review', allowedRoles: 'Moderator, Super Admin', autoManual: 'Manual', conditions: '—' },
    { from: 'In Review', to: 'Decision Required', allowedRoles: 'Moderator, Super Admin', autoManual: 'Manual', conditions: '—' },
    { from: 'In Review', to: 'Escalated', allowedRoles: 'Super Admin', autoManual: 'Manual', conditions: 'Only if Severity Level ≥ 3' },
    { from: 'Decision Required', to: 'Action Applied', allowedRoles: 'Moderator, Super Admin', autoManual: 'Manual', conditions: '—' },
    { from: 'Action Applied', to: 'Closed', allowedRoles: 'Moderator, Super Admin', autoManual: 'Manual', conditions: '—' },
    { from: 'Escalated', to: 'In Review', allowedRoles: 'Super Admin', autoManual: 'Manual', conditions: 'Senior Admin only' },
  ];
};

// Helper function to generate routing rules
const generateRoutingRules = (contentType) => {
  return [
    {
      condition: `Content Type = ${contentType}`,
      routedToTeam: `${contentType} Moderators`,
      defaultAssignee: '—',
      autoAssign: 'No',
      notes: `Standard routing for all ${contentType.toLowerCase()}.`,
    },
    {
      condition: `Content Type = ${contentType} AND Severity Level ≥ 3`,
      routedToTeam: `Senior ${contentType} Moderators`,
      defaultAssignee: 'Admin#45',
      autoAssign: 'Yes',
      notes: 'High severity cases always to senior team.',
    },
  ];
};

// Helper function to generate escalation rules
const generateEscalationRules = () => {
  return [
    {
      triggerType: 'Severity Change',
      triggerCondition: 'Severity changed to Level 4',
      escalateTo: 'Compliance Manager',
      escalationAction: 'Set stage to Escalated, notify team via email.',
      notes: 'Used for serious abuse or fraud.',
    },
    {
      triggerType: 'Repeat Violations',
      triggerCondition: 'Same user flagged 3+ times in 30 days',
      escalateTo: 'Risk & Safety Team',
      escalationAction: 'Set stage to Escalated, notify team.',
      notes: 'Pattern detection for repeat offenders.',
    },
  ];
};

// Helper function to generate completion rules
const generateCompletionRules = () => {
  return [
    {
      outcomeCode: 'VIOLATION-ENFORCED',
      outcomeLabel: 'Violation Enforced',
      conditions: 'At least one enforcement action applied.',
      allowsClosure: 'Yes',
      requiresEnforcement: 'Yes',
    },
    {
      outcomeCode: 'NO-VIOLATION',
      outcomeLabel: 'No Violation Found',
      conditions: 'All evidence reviewed, no rule broken.',
      allowsClosure: 'Yes',
      requiresEnforcement: 'No',
    },
    {
      outcomeCode: 'DUPLICATE-REPORT',
      outcomeLabel: 'Duplicate Report',
      conditions: 'Same case already processed.',
      allowsClosure: 'Yes',
      requiresEnforcement: 'No',
    },
  ];
};

// Helper function to generate audit logs
const generateAuditLogs = (workflowId) => {
  const baseTime = new Date();
  const logs = [];
  const admins = ['Sarah K.', 'Michael T.', 'Helen P.', 'John S.'];
  const sections = ['Overview', 'Stages & Transitions', 'Routing Rules', 'Escalation Rules', 'Completion Rules'];

  for (let i = 0; i < 15; i++) {
    const timestamp = new Date(baseTime.getTime() - i * 24 * 60 * 60 * 1000);
    const admin = admins[Math.floor(Math.random() * admins.length)];
    const adminId = Math.floor(Math.random() * 50) + 1;
    const section = sections[Math.floor(Math.random() * sections.length)];
    const changeSummary = `Updated ${section.toLowerCase()}`;
    const previousValue = 'Previous configuration';
    const newValue = 'New configuration';

    logs.push({
      id: `log-${i}`,
      timestamp: timestamp.toISOString(),
      admin: `${admin}`,
      section,
      changeSummary,
      previousValue,
      newValue,
    });
  }

  return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

// Helper function to generate admin notes
const generateNotes = (workflowId) => {
  const baseTime = new Date();
  return [
    {
      id: 'note-1',
      createdBy: 'Admin — Alex Chen (Moderation)',
      createdAt: formatNoteDateTime(new Date(baseTime.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString()),
      createdAtISO: new Date(baseTime.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      author: 'Alex Chen',
      team: 'Moderation',
      adminName: 'Admin — Alex Chen (Moderation)',
      noteText: 'Workflow created to standardize moderation process. All Level 4 cases must be reviewed by senior team.',
      noteContent: 'Workflow created to standardize moderation process. All Level 4 cases must be reviewed by senior team.',
      content: 'Workflow created to standardize moderation process. All Level 4 cases must be reviewed by senior team.',
    },
    {
      id: 'note-2',
      createdBy: 'Admin — Alex Chen (Moderation)',
      createdAt: formatNoteDateTime(new Date(baseTime.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString()),
      createdAtISO: new Date(baseTime.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      author: 'Alex Chen',
      team: 'Moderation',
      adminName: 'Admin — Alex Chen (Moderation)',
      noteText: 'Updated routing rules to include auto-assignment for high severity cases.',
      noteContent: 'Updated routing rules to include auto-assignment for high severity cases.',
      content: 'Updated routing rules to include auto-assignment for high severity cases.',
    },
  ];
};

const WorkflowDetail = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const workflowFromState = location.state?.workflow;

  const [activeTab, setActiveTab] = useState('overview');
  const [detail, setDetail] = useState(() => generateWorkflowDetail(workflowId, workflowFromState));

  // Admin Notes state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    noteText: '',
  });
  const [notes, setNotes] = useState(() => generateNotes(workflowId));

  const stages = useMemo(() => generateStages(), []);
  const transitions = useMemo(() => generateTransitions(), []);
  const routingRules = useMemo(() => generateRoutingRules(detail.contentType), [detail.contentType]);
  const escalationRules = useMemo(() => generateEscalationRules(), []);
  const completionRules = useMemo(() => generateCompletionRules(), []);
  const auditLogs = useMemo(() => generateAuditLogs(workflowId), [workflowId]);

  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNoteForm({ noteText: '' });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText || noteForm.noteText.trim() === '') {
      alert('Note is required');
      return;
    }

    const now = new Date();
    const timestamp = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    
    // Add new note
    // In real app, get admin name and team from auth context
    const newNote = {
      id: `note-${Date.now()}`,
      createdBy: 'Admin — Alex Chen (Moderation)',
      createdAt: formatNoteDateTime(now.toISOString()),
      author: 'Alex Chen',
      team: 'Moderation',
      adminName: 'Admin — Alex Chen (Moderation)',
      noteText: noteForm.noteText.trim(),
      noteContent: noteForm.noteText.trim(),
      content: noteForm.noteText.trim(),
      createdAtISO: now.toISOString()
    };
    
    setNotes(prev => [newNote, ...prev]);
    closeNotesModal();
  };

  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="space-y-1">
          <AdminSupportingText>
            Workflow Name
          </AdminSupportingText>
          <AdminMainText className="text-offblack font-semibold">
            {detail.workflowName}
          </AdminMainText>
        </div>

        <div className="space-y-1">
          <AdminSupportingText>
            Content Type
          </AdminSupportingText>
          <AdminMainText className="text-offblack font-semibold">
            {detail.contentType}
          </AdminMainText>
        </div>

        <div className="space-y-1">
          <AdminSupportingText>
            Status
          </AdminSupportingText>
          <AdminStatusBadge status={detail.status} label={detail.status} />
        </div>

        <div className="space-y-1">
          <AdminSupportingText>
            Default Entry Stage
          </AdminSupportingText>
          <AdminMainText className="text-offblack font-semibold">
            {detail.defaultEntryStage}
          </AdminMainText>
        </div>

        <div className="space-y-1">
          <AdminSupportingText>
            Default Exit Stage
          </AdminSupportingText>
          <AdminMainText className="text-offblack font-semibold">
            {detail.defaultExitStage}
          </AdminMainText>
        </div>

        <div className="space-y-1 lg:col-span-3">
          <AdminSupportingText>
            Description
          </AdminSupportingText>
          <AdminMainText className="text-offblack font-semibold">
            {detail.description}
          </AdminMainText>
        </div>
    </div>
  );

  const renderStagesAndTransitions = () => (
    <div className="space-y-8">
        {/* Stages Table */}
        <div>
          <AdminPanelTitle className="text-offblack">
            Stages
          </AdminPanelTitle>
          <div className="overflow-x-auto">
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(150px, 1fr) minmax(200px, 1.5fr)', width: '100%' }}>
              <div className="flex items-center">
                <span className="font-semibold">Stage Name</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Stage Order</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Required?</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">SLA Target</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Visible To</span>
              </div>
            </div>
            {stages.map((stage, index) => (
              <div
                key={index}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
                style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(150px, 1fr) minmax(200px, 1.5fr)', width: '100%' }}
              >
                <div className="flex items-center">
                  <span className="">{stage.name}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{stage.order}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{stage.required ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{stage.slaTarget}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs">{stage.visibleTo}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transitions Table */}
        <div>
          <AdminPanelTitle className="text-offblack">
            Transitions
          </AdminPanelTitle>
          <div className="overflow-x-auto">
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(120px, 1fr) minmax(120px, 1fr) minmax(180px, 1.5fr) minmax(100px, 1fr) minmax(200px, 1.5fr)', width: '100%' }}>
              <div className="flex items-center">
                <span className="font-semibold">From Stage</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">To Stage</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Allowed Roles</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Auto / Manual</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Conditions</span>
              </div>
            </div>
            {transitions.map((transition, index) => (
              <div
                key={index}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
                style={{ gridTemplateColumns: 'minmax(120px, 1fr) minmax(120px, 1fr) minmax(180px, 1.5fr) minmax(100px, 1fr) minmax(200px, 1.5fr)', width: '100%' }}
              >
                <div className="flex items-center">
                  <span className="">{transition.from}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{transition.to}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs">{transition.allowedRoles}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{transition.autoManual}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs">{transition.conditions}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
    </div>
  );

  const renderRoutingRules = () => (
    <div>
      <AdminPanelTitle className="text-offblack mb-4">
        Routing Rules
      </AdminPanelTitle>
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="overflow-x-auto">
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(250px, 2fr) minmax(200px, 1.5fr) minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(200px, 1.5fr)', width: '100%' }}>
            <div className="flex items-center">
              <span className="font-semibold">Condition</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Routed To Team</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Default Assignee</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Auto Assign?</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Notes</span>
            </div>
          </div>
          {routingRules.map((rule, index) => (
            <div
              key={index}
              className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
              style={{ gridTemplateColumns: 'minmax(250px, 2fr) minmax(200px, 1.5fr) minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(200px, 1.5fr)', width: '100%' }}
            >
              <div className="flex items-center">
                <span className="text-xs">{rule.condition}</span>
              </div>
              <div className="flex items-center">
                <span className="">{rule.routedToTeam}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{rule.defaultAssignee}</span>
              </div>
              <div className="flex items-center">
                <span className="">{rule.autoAssign}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{rule.notes}</span>
              </div>
            </div>
          ))}
        </div>
      </DashboardMainContainer>
    </div>
  );

  const renderEscalationRules = () => (
    <div>
      <AdminPanelTitle className="text-offblack mb-4">
        Escalation Rules
      </AdminPanelTitle>
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="overflow-x-auto">
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(200px, 1.5fr) minmax(180px, 1.3fr) minmax(250px, 2fr) minmax(200px, 1.5fr)', width: '100%' }}>
            <div className="flex items-center">
              <span className="font-semibold">Trigger Type</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Trigger Condition</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Escalate To</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Escalation Action</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Notes</span>
            </div>
          </div>
          {escalationRules.map((rule, index) => (
            <div
              key={index}
              className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
              style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(200px, 1.5fr) minmax(180px, 1.3fr) minmax(250px, 2fr) minmax(200px, 1.5fr)', width: '100%' }}
            >
              <div className="flex items-center">
                <span className="">{rule.triggerType}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{rule.triggerCondition}</span>
              </div>
              <div className="flex items-center">
                <span className="">{rule.escalateTo}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{rule.escalationAction}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{rule.notes}</span>
              </div>
            </div>
          ))}
        </div>
      </DashboardMainContainer>
    </div>
  );

  const renderCompletionRules = () => (
    <div>
      <AdminPanelTitle className="text-offblack mb-4">
        Completion Criteria
      </AdminPanelTitle>
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="overflow-x-auto">
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(180px, 1.3fr) minmax(250px, 2fr) minmax(120px, 1fr) minmax(150px, 1.2fr)', width: '100%' }}>
            <div className="flex items-center">
              <span className="font-semibold">Outcome Code</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Outcome Label</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Conditions</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Allows Closure?</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Requires Enforcement?</span>
            </div>
          </div>
          {completionRules.map((rule, index) => (
            <div
              key={index}
              className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
              style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(180px, 1.3fr) minmax(250px, 2fr) minmax(120px, 1fr) minmax(150px, 1.2fr)', width: '100%' }}
            >
              <div className="flex items-center">
                <span className="text-xs">{rule.outcomeCode}</span>
              </div>
              <div className="flex items-center">
                <span className="">{rule.outcomeLabel}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{rule.conditions}</span>
              </div>
              <div className="flex items-center">
                <span className="">{rule.allowsClosure}</span>
              </div>
              <div className="flex items-center">
                <span className="">{rule.requiresEnforcement}</span>
              </div>
            </div>
          ))}
        </div>
      </DashboardMainContainer>
    </div>
  );

  const renderAuditLogs = () => (
    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
      <div className="overflow-x-auto">
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(200px, 1.5fr) minmax(200px, 1.5fr) minmax(200px, 1.5fr)', width: '100%' }}>
            <div className="flex items-center">
              <span className="font-semibold">Timestamp</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Admin</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Section</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Change Summary</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Previous Value</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">New Value</span>
            </div>
          </div>
          {auditLogs.map((log) => (
            <div
              key={log.id}
              className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
              style={{ gridTemplateColumns: 'minmax(150px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(200px, 1.5fr) minmax(200px, 1.5fr) minmax(200px, 1.5fr)', width: '100%' }}
            >
              <div className="flex items-center">
                <span className="text-xs">{formatDateTime(log.timestamp)}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{log.admin}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{log.section}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs">{log.changeSummary}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs truncate" title={log.previousValue}>{log.previousValue}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs truncate" title={log.newValue}>{log.newValue}</span>
              </div>
            </div>
          ))}
      </div>
    </DashboardMainContainer>
  );

  const renderAdminNotes = () => (
    <div>
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex justify-end items-center w-full">
            <AdminRowAction onClick={() => openNotesModal()}>
              Add note
            </AdminRowAction>
          </div>
        </div>
        
        {/* Notes Table */}
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          {/* Header */}
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
            <div className="flex items-center">
              <span className="font-semibold">Created At</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Added By</span>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Note</AdminTableHeader></div>
          </div>
          
          {/* Rows - Sort by date (newest first) */}
          {(() => {
            const sortedNotes = [...notes].sort((a, b) => {
              const aDate = new Date(a.createdAtISO || a.createdAt || 0);
              const bDate = new Date(b.createdAtISO || b.createdAt || 0);
              return bDate - aDate;
            });

            return sortedNotes.length > 0 ? sortedNotes.map((note) => {
              const noteContent = note.noteContent || note.content || note.noteText || '';
              const createdAt = note.createdAtISO ? formatNoteDateTime(note.createdAtISO) : 
                               (note.createdAt || '—');
              
              const addedByDisplay = getAdminNoteAddedByDisplay(note);

              return (
                <div key={note.id} className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" style={{ gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(250px, 2fr) minmax(400px, 4fr)' }}>
                  <div className="flex items-start">
                    <span className="">{createdAt}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium">{addedByDisplay}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="whitespace-pre-wrap">{noteContent}</span>
                  </div>
                </div>
              );
            }) : (
              <div className="py-8 text-center">
                <span className="text-mediumgray">No notes yet</span>
              </div>
            );
          })()}
        </DashboardMainContainer>

        {/* Add Note Modal */}
        {notesModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeNotesModal}>
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <AdminPanelTitle className="text-offblack">
                  Add Note
                </AdminPanelTitle>
                <button
                  onClick={closeNotesModal}
                  className="text-offblack hover:text-primaryblue"
                >
                  <i className="fi fi-rr-cross dashboard-icon"></i>
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Note - Required */}
                <div>
                  <AdminDetailFieldLabel className="mb-1.5">
                    Note <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <textarea
                    value={noteForm.noteText}
                    onChange={(e) => setNoteForm(prev => ({ ...prev, noteText: e.target.value }))}
                    className={adminFormTextareaClasses()}
                    placeholder="Enter note..."
                    required
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <AdminFormSecondaryAction type="button" onClick={closeNotesModal}
                    >Cancel</AdminFormSecondaryAction>
                  <AdminHeaderAction onClick={handleNoteSave}>
                    Save note
                  </AdminHeaderAction>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );

  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'stages', label: 'Stages & Transitions' },
    { id: 'routing', label: 'Routing Rules' },
    { id: 'escalation', label: 'Escalation Rules' },
    { id: 'completion', label: 'Completion Rules' },
    { id: 'audit', label: 'Audit Logs' },
    { id: 'notes', label: 'Admin Notes' },
  ], []);

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'stages':
        return renderStagesAndTransitions();
      case 'routing':
        return renderRoutingRules();
      case 'escalation':
        return renderEscalationRules();
      case 'completion':
        return renderCompletionRules();
      case 'audit':
        return renderAuditLogs();
      case 'notes':
        return renderAdminNotes();
      default:
        return renderOverview();
    }
  };

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="space-y-2">
                  <AdminPanelTitle className="text-offblack">
                    {detail.workflowName}
                  </AdminPanelTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <AdminStatusBadge status={detail.status} label={detail.status} />
                    <AdminMetadataText>
                      {detail.contentType}
                    </AdminMetadataText>
                    <AdminMetadataText>
                      {detail.version}
                    </AdminMetadataText>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    <AdminMetadataText>
                      Last Updated: {formatDateTime(detail.lastUpdated)}
                    </AdminMetadataText>
                    <AdminMetadataText>
                      Updated By: {detail.updatedBy}
                    </AdminMetadataText>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div className={adminDetailTabContentClassName(activeTab)}>
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default WorkflowDetail;

